/**
 * Score this browser session as human or bot, once per session.
 *
 * Mints one reCAPTCHA v3 token and posts it to /api/analytics/session-score,
 * which verifies it and writes a `bot_score` row joined to the session by id.
 * Called on every page view; everything after the first success is a
 * sessionStorage read.
 *
 * Nothing here may affect the visit. Every failure -- script not loaded,
 * blocked by an extension, Google slow, route down -- is silent, and leaves the
 * session unmarked so the next page view tries again. Failures are NOT sent to
 * error tracking: reCAPTCHA is blocked by a large share of ad blockers, and
 * that would be a stream of errors about visitors, not about the code.
 */
import { RECAPTCHA_SITE_KEY, recaptchaClient } from './recaptcha-client';
import { getSessionId } from './tracking-context';
import { getRankingVariant } from './ranking-variant-store';
import { isAnalyticsDisabled } from './analytics-enabled';
import { SESSION_SCORE_ACTION, PARTNER_CODE_MAX, PARTNER_CODE_PATTERN } from '../schemas/api/analytics/session-score';
import { getPartner } from './partner';
import { getAnalytics } from './analytics';

const SCORED_KEY = 'sw_bot_scored_session';
const ENDPOINT = '/api/analytics/session-score';

/** The script loads `afterInteractive`, usually after the first page view fires. */
const WAIT_FOR_SCRIPT_MS = 10_000;
const POLL_MS = 250;

type Grecaptcha = {
  ready: (callback: () => void) => void;
  execute: (siteKey: string, options: { action: string }) => Promise<string>;
};

/**
 * The publisher that sent this visit, if we can tell.
 *
 * Read here rather than left to the server's cookie, because on a landing the
 * code is still only in the URL -- `PartnerProvider` writes the cookie from a
 * mount effect, and this request can win that race. The server falls back to
 * the cookie for every page view after the first.
 *
 * Anything that does not look like a partner code is dropped rather than sent:
 * the server would refuse it anyway, and a refused body costs the whole score.
 */
function partnerCode(): string {
  try {
    const partner = getPartner();
    if (typeof partner !== 'string') return '';
    const trimmed = partner.trim();
    if (trimmed.length > PARTNER_CODE_MAX || !PARTNER_CODE_PATTERN.test(trimmed)) return '';
    return trimmed;
  // eslint-disable-next-line sellwild-code/catch-must-log -- storage refused; attribution is optional and the score is not
  } catch {
    return '';
  }
}

/**
 * How far this page's bot check got. An error row from a session reCAPTCHA
 * never scored cannot be told apart from a buyer's; this says whether the
 * script never loaded, the check was still running, or it failed. On
 * 2026-10-26/27 two of six card-form failures were unscored, and nothing on
 * either row said why.
 */
export type BotCheckStep =
  | 'not-started'
  | 'disabled'
  | 'waiting-for-script'
  | 'no-script'
  | 'minting'
  | 'no-token'
  | 'posting'
  | 'scored'
  | 'refused'
  | 'server-error'
  | 'threw';

let step: BotCheckStep = 'not-started';
/** The score the server answered with on this page, when it said one. */
let lastScore: number | null = null;

/** Where the bot check stands, for an error row to carry. */
export function botCheckState(): { step: BotCheckStep; score: number | null } {
  return { step, score: lastScore };
}

/** The score in a session-score answer, or null when it carries none. */
async function readScore(response: Response): Promise<number | null> {
  try {
    const body: unknown = await response.json();
    const score = (body as { score?: unknown } | null)?.score;
    return typeof score === 'number' ? score : null;
  // eslint-disable-next-line sellwild-code/catch-must-log -- a body we cannot read only means the row carries no score; the step still says it was scored
  } catch {
    return null;
  }
}

/**
 * Why this session ended with no score, sent once per session as a
 * `bot_score_skipped` event: the step it stopped at, how long it waited, and
 * whether the recaptcha.net copy was on the page. Prod 2026-11-04: 108 of 290
 * real checkout visitors in a week had no score, and nothing said why.
 */
export const BOT_SCORE_SKIPPED_EVENT = 'bot_score_skipped';
let skippedSentFor = '';

function reportSkipped(sessionId: string, startedAt: number): void {
  if (skippedSentFor === sessionId) return;
  skippedSentFor = sessionId;
  getAnalytics()?.track({
    event_type: BOT_SCORE_SKIPPED_EVENT,
    properties: {
      step,
      waited_ms: String(Date.now() - startedAt),
      fallback: typeof document !== 'undefined' && document.getElementById('recaptcha-fallback') ? '1' : '0',
    },
  });
}

/** The session currently being scored, so rapid navigations send one token. */
let inFlight = '';

/** Mirror of the stored mark, for browsers that refuse sessionStorage. */
let scoredInMemory = '';

function readScored(): string {
  try {
    return sessionStorage.getItem(SCORED_KEY) || scoredInMemory;
  // eslint-disable-next-line sellwild-code/catch-must-log -- storage refused (privacy mode); the in-memory mark answers instead
  } catch {
    return scoredInMemory;
  }
}

function markScored(sessionId: string): void {
  scoredInMemory = sessionId;
  try {
    sessionStorage.setItem(SCORED_KEY, sessionId);
  // eslint-disable-next-line sellwild-code/catch-must-log -- storage refused; scoredInMemory already holds the mark until a reload
  } catch {
    // Nothing to do.
  }
}

async function waitForRecaptcha(): Promise<Grecaptcha | undefined> {
  const deadline = Date.now() + WAIT_FOR_SCRIPT_MS;
  while (Date.now() < deadline) {
    const grecaptcha = recaptchaClient();
    if (grecaptcha?.execute && grecaptcha.ready) return grecaptcha as Grecaptcha;
    await new Promise((resolve) => setTimeout(resolve, POLL_MS));
  }
  return undefined;
}

/** Called from useAnalytics' effect, so only ever in a browser. */
export async function scoreSessionOnce(): Promise<void> {
  if (isAnalyticsDisabled()) {
    step = 'disabled';
    return;
  }

  const sessionId = getSessionId();
  if (!sessionId || sessionId === inFlight) return;
  if (sessionId === readScored()) {
    step = 'scored';
    return;
  }
  inFlight = sessionId;
  const startedAt = Date.now();

  try {
    step = 'waiting-for-script';
    const grecaptcha = await waitForRecaptcha();
    if (!grecaptcha) {
      step = 'no-script';
      return;
    }

    step = 'minting';
    await new Promise<void>((resolve) => grecaptcha.ready(resolve));
    const token = await grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: SESSION_SCORE_ACTION });
    if (!token) {
      step = 'no-token';
      return;
    }

    step = 'posting';
    const response = await fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ token, ranking_variant: getRankingVariant(), partner: partnerCode() || undefined }),
    });

    // Only a recorded score ends the session's attempts. A 5xx retries on the
    // next page view; a 4xx would repeat identically, so it ends them too.
    if (response.ok || (response.status >= 400 && response.status < 500)) markScored(sessionId);
    if (response.ok) {
      step = 'scored';
      lastScore = await readScore(response);
    } else {
      step = response.status < 500 ? 'refused' : 'server-error';
    }
  // eslint-disable-next-line sellwild-code/catch-must-log -- ad blockers stop reCAPTCHA for many real visitors; logging it would flood error_logs with visitor state, not defects (see header)
  } catch {
    // Silent by design; see the header. The step is the record.
    step = 'threw';
  } finally {
    inFlight = '';
    if (step !== 'scored') reportSkipped(sessionId, startedAt);
  }
}

/** Test-only: forget the in-flight guard between cases. */
export function resetSessionBotScoreForTests(): void {
  inFlight = '';
  scoredInMemory = '';
  step = 'not-started';
  lastScore = null;
  skippedSentFor = '';
}
